import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Breadcrumb, LocalizedDateTimeInput, Spinner } from '../../../shared/components';
import { formatDateTime, parseDateTimeInputValue } from '../../../shared/utils/localization';
import { useToast } from '../../notifications';
import type { ConversationAiOperationItem } from '../../conversations/services/conversationService';
import { listTenantAiOperations } from '../services/aiActivityService';
import styles from './AiActivityPage.module.css';

const formatPayload = (value?: string) => {
  if (!value?.trim()) {
    return '';
  }

  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const formatRunLabel = (runId: string) => (runId.length > 8 ? `${runId.slice(0, 8)}…` : runId);

const formatConversationLabel = (conversationId: string) => (
  conversationId.length > 8 ? `${conversationId.slice(0, 8)}…` : conversationId
);

export const AiActivityPage: React.FC = () => {
  const { t } = useTranslation(['aiActivity', 'conversations', 'common', 'nav']);
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [items, setItems] = React.useState<ConversationAiOperationItem[]>([]);
  const [hasMore, setHasMore] = React.useState(false);
  const [nextBefore, setNextBefore] = React.useState<string | undefined>(undefined);
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());

  const [statusFilter, setStatusFilter] = React.useState('all');
  const [pluginFilter, setPluginFilter] = React.useState('');
  const [triggeredByFilter, setTriggeredByFilter] = React.useState('all');
  const [functionNameFilter, setFunctionNameFilter] = React.useState('');
  const [conversationIdFilter, setConversationIdFilter] = React.useState('');
  const [runIdFilter, setRunIdFilter] = React.useState('');
  const [searchFilter, setSearchFilter] = React.useState('');
  const [afterFilter, setAfterFilter] = React.useState('');

  React.useEffect(() => {
    const conversationId = searchParams.get('conversationId')?.trim();
    const runId = searchParams.get('runId')?.trim();

    if (conversationId) {
      setConversationIdFilter(conversationId);
    }

    if (runId) {
      setRunIdFilter(runId);
    }
  }, [searchParams]);

  const pluginOptions = React.useMemo(
    () => Array.from(new Set(items.map((item) => item.pluginName).filter(Boolean))).sort(),
    [items],
  );

  const loadItems = React.useCallback(async (append: boolean) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError(null);
    }

    try {
      const response = await listTenantAiOperations({
        limit: 50,
        before: append ? nextBefore : undefined,
        after: afterFilter.trim() ? parseDateTimeInputValue(afterFilter)?.toISOString() : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        plugin: pluginFilter.trim() || undefined,
        triggeredBy: triggeredByFilter !== 'all' ? triggeredByFilter : undefined,
        functionName: functionNameFilter.trim() || undefined,
        conversationId: conversationIdFilter.trim() || undefined,
        runId: runIdFilter.trim() || undefined,
        search: searchFilter.trim() || undefined,
      });

      setItems((current) => {
        if (!append) {
          return response.items;
        }

        const existingIds = new Set(current.map((item) => item.id));
        return [...current, ...response.items.filter((item) => !existingIds.has(item.id))];
      });
      setHasMore(response.hasMore);
      setNextBefore(response.nextBefore);
    } catch (loadError) {
      const message = loadError instanceof Error
        ? loadError.message
        : t('failedToLoad', { ns: 'conversations' });
      setError(message);
      if (!append) {
        setItems([]);
        setHasMore(false);
        setNextBefore(undefined);
      }
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [
    afterFilter,
    conversationIdFilter,
    functionNameFilter,
    nextBefore,
    pluginFilter,
    runIdFilter,
    searchFilter,
    statusFilter,
    t,
    triggeredByFilter,
  ]);

  const loadItemsRef = React.useRef(loadItems);
  loadItemsRef.current = loadItems;

  React.useEffect(() => {
    void loadItemsRef.current(false);
  }, [
    statusFilter,
    pluginFilter,
    triggeredByFilter,
    functionNameFilter,
    conversationIdFilter,
    runIdFilter,
    searchFilter,
    afterFilter,
  ]);

  const copyPayload = async (value: string | undefined, label: string) => {
    if (!value?.trim()) {
      return;
    }

    try {
      await navigator.clipboard.writeText(formatPayload(value));
      showToast(t('copiedPayload', { label }), 'success');
    } catch {
      showToast(t('failedToCopyPayload', { label }), 'error');
    }
  };

  const copyVisibleExport = async () => {
    if (items.length === 0) {
      return;
    }

    try {
      await navigator.clipboard.writeText(JSON.stringify({
        exportedAt: new Date().toISOString(),
        items,
      }, null, 2));
      showToast(t('copiedExport'), 'success');
    } catch {
      showToast(t('failedToCopyExport'), 'error');
    }
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className={`company-admin-page ${styles.page}`}>
      <Breadcrumb items={[
        { label: t('ai', { ns: 'nav' }), path: '/ai/knowledge' },
        { label: t('title'), path: '/ai/activity' },
      ]} />

      <div className={styles.hero}>
        <div>
          <h2>{t('title')}</h2>
          <p className={styles.intro}>{t('subtitle')}</p>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={loading || items.length === 0}
            onClick={() => void copyVisibleExport()}
          >
            {t('copyExport')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={loading}
            onClick={() => void loadItems(false)}
          >
            {loading ? t('loading', { ns: 'common' }) : t('refresh')}
          </button>
        </div>
      </div>

      <section className={styles.filters} aria-label={t('filtersTitle')}>
        <h3 className={styles.filtersTitle}>{t('filtersTitle')}</h3>

        <div className={styles.filterRow}>
          <label className={styles.filterField}>
            <span>{t('status')}</span>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="all">{t('allStatuses')}</option>
              <option value="Succeeded">{t('succeeded')}</option>
              <option value="Failed">{t('failed')}</option>
            </select>
          </label>
          <label className={styles.filterField}>
            <span>{t('plugin')}</span>
            <select value={pluginFilter} onChange={(event) => setPluginFilter(event.target.value)}>
              <option value="">{t('allPlugins')}</option>
              {pluginOptions.map((pluginName) => (
                <option key={pluginName} value={pluginName}>{pluginName}</option>
              ))}
            </select>
          </label>
          <label className={styles.filterField}>
            <span>{t('triggeredBy')}</span>
            <select value={triggeredByFilter} onChange={(event) => setTriggeredByFilter(event.target.value)}>
              <option value="all">{t('allSources')}</option>
              <option value="ai-assistant">{t('aiAssistant', { ns: 'conversations' })}</option>
              <option value="employee">{t('employee', { ns: 'conversations' })}</option>
              <option value="system">{t('system', { ns: 'conversations' })}</option>
            </select>
          </label>
        </div>

        <div className={styles.filterRow}>
          <label className={styles.filterField}>
            <span>{t('functionName')}</span>
            <input
              value={functionNameFilter}
              onChange={(event) => setFunctionNameFilter(event.target.value)}
              placeholder={t('functionNamePlaceholder')}
            />
          </label>
          <label className={styles.filterField}>
            <span>{t('conversationId')}</span>
            <input
              value={conversationIdFilter}
              onChange={(event) => setConversationIdFilter(event.target.value)}
              placeholder={t('conversationIdPlaceholder')}
            />
          </label>
          <label className={styles.filterField}>
            <span>{t('runId')}</span>
            <input
              value={runIdFilter}
              onChange={(event) => setRunIdFilter(event.target.value)}
              placeholder={t('runIdPlaceholder')}
            />
          </label>
        </div>

        <div className={styles.filterRowWide}>
          <label className={styles.filterField}>
            <span>{t('search')}</span>
            <input
              value={searchFilter}
              onChange={(event) => setSearchFilter(event.target.value)}
              placeholder={t('searchPlaceholder')}
            />
          </label>
          <div className={styles.filterField}>
            <span>{t('after')}</span>
            <LocalizedDateTimeInput
              className={styles.filterDateTime}
              value={afterFilter}
              onValueChange={setAfterFilter}
              aria-label={t('after')}
            />
          </div>
        </div>
      </section>

      {loading ? (
        <div className={styles.state}>
          <Spinner text={t('loadingAiActivity', { ns: 'conversations' })} />
        </div>
      ) : error ? (
        <div className={`${styles.state} ${styles.stateError}`}>{error}</div>
      ) : items.length === 0 ? (
        <div className={styles.state}>{t('noResults')}</div>
      ) : (
        <div className={styles.list}>
          {items.map((item) => {
            const isExpanded = expandedIds.has(item.id);
            const isFailed = item.status === 'Failed';

            return (
              <div
                key={item.id}
                className={[styles.item, isFailed ? styles.itemFailed : ''].filter(Boolean).join(' ')}
              >
                <button
                  type="button"
                  className={styles.itemSummary}
                  onClick={() => toggleExpanded(item.id)}
                >
                  <div className={styles.itemMeta}>
                    <span className={`${styles.pill} ${styles.pillPlugin}`}>{item.pluginName}</span>
                    <span className={`${styles.pill} ${styles.pillFunction}`}>{item.functionName}</span>
                    <span className={`${styles.pill} ${isFailed ? styles.pillFailed : styles.pillSucceeded}`}>
                      {item.status}
                    </span>
                    <span className={`${styles.pill} ${styles.pillQuiet}`}>{item.triggeredBy}</span>
                    {item.conversationId ? (
                      <Link
                        to={`/conversations/${item.conversationId}`}
                        className={styles.conversationLink}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {t('openConversation', { id: formatConversationLabel(item.conversationId) })}
                      </Link>
                    ) : null}
                    {item.runId ? (
                      <span className={`${styles.pill} ${styles.pillQuiet}`}>{formatRunLabel(item.runId)}</span>
                    ) : null}
                    <span className={`${styles.pill} ${styles.pillTime}`}>{formatDateTime(item.createdAt)}</span>
                  </div>
                  <span
                    className={[styles.chevron, isExpanded ? styles.chevronOpen : ''].filter(Boolean).join(' ')}
                    aria-hidden="true"
                  >
                    <svg viewBox="0 0 16 16" fill="none">
                      <path
                        d="M4 6l4 4 4-4"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </button>
                {item.operationDetails ? (
                  <div className={styles.operationDetails}>{item.operationDetails}</div>
                ) : null}
                {isExpanded ? (
                  <div className={styles.itemDetails}>
                    <div className={styles.payloadGrid}>
                      {item.inputJson ? (
                        <section className={styles.payloadBlock}>
                          <div className={styles.payloadHeader}>
                            <span>{t('input', { ns: 'conversations' })}</span>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => void copyPayload(item.inputJson, t('input', { ns: 'conversations' }))}
                            >
                              {t('copy', { ns: 'common' })}
                            </button>
                          </div>
                          <pre className={styles.payloadPre}>{formatPayload(item.inputJson)}</pre>
                        </section>
                      ) : null}
                      {item.outputJson ? (
                        <section className={styles.payloadBlock}>
                          <div className={styles.payloadHeader}>
                            <span>{t('output', { ns: 'conversations' })}</span>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => void copyPayload(item.outputJson, t('output', { ns: 'conversations' }))}
                            >
                              {t('copy', { ns: 'common' })}
                            </button>
                          </div>
                          <pre className={styles.payloadPre}>{formatPayload(item.outputJson)}</pre>
                        </section>
                      ) : null}
                      {item.error ? (
                        <section className={styles.payloadBlock}>
                          <div className={styles.payloadHeader}>
                            <span>{t('error', { ns: 'common' })}</span>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => void copyPayload(item.error, t('error', { ns: 'common' }))}
                            >
                              {t('copy', { ns: 'common' })}
                            </button>
                          </div>
                          <pre className={styles.payloadPre}>{formatPayload(item.error)}</pre>
                        </section>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {hasMore && !loading && !error ? (
        <div className={styles.footer}>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={loadingMore}
            onClick={() => void loadItems(true)}
          >
            {loadingMore ? t('loading', { ns: 'common' }) : t('loadMoreAiActivity', { ns: 'conversations' })}
          </button>
        </div>
      ) : null}
    </div>
  );
};
